import { useState, useMemo } from 'react';
import { campaigns as initialCampaigns, type Campaign, type CampaignSource } from '@/mocks/campaigns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { useTheme } from '@/context/ThemeContext';

const sources: CampaignSource[] = ["Google Ads", "Meta Ads", "WhatsApp", "Instagram Bio", "Tavsiye"];
const sourceColors: Record<CampaignSource, string> = {
  "Google Ads": "#2D5F3E",
  "Meta Ads": "#3B82F6",
  "WhatsApp": "#10B981",
  "Instagram Bio": "#C9A962",
  "Tavsiye": "#8B5CF6",
};

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

export default function KampanyalarPage() {
  const { theme } = useTheme();
  const [campaignList, setCampaignList] = useState<Campaign[]>(initialCampaigns);
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const [form, setForm] = useState<Partial<Campaign>>({
    name: '',
    startDate: '',
    endDate: '',
    source: 'Google Ads',
    budget: 0,
    notes: '',
    active: true,
    customers: 0,
    appointments: 0,
    sales: 0,
    revenue: 0,
  });

  const filtered = useMemo(() => {
    let data = [...campaignList];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(c => c.name.toLowerCase().includes(q));
    }
    if (sourceFilter !== 'all') data = data.filter(c => c.source === sourceFilter);
    if (activeFilter !== 'all') data = data.filter(c => activeFilter === 'active' ? c.active : !c.active);
    return data;
  }, [searchQuery, sourceFilter, activeFilter, campaignList]);

  const stats = useMemo(() => {
    const totalCustomers = filtered.reduce((sum, c) => sum + c.customers, 0);
    const totalRevenue = filtered.reduce((sum, c) => sum + c.revenue, 0);
    const totalBudget = filtered.reduce((sum, c) => sum + c.budget, 0);
    const avgConversion = totalCustomers > 0 ? Math.round((filtered.reduce((sum, c) => sum + c.sales, 0) / totalCustomers) * 100) : 0;
    return { totalCustomers, totalRevenue, totalBudget, avgConversion };
  }, [filtered]);

  const chartData = useMemo(() => {
    return filtered.map(c => ({
      name: c.name.length > 20 ? c.name.substring(0, 20) + '...' : c.name,
      customers: c.customers,
      appointments: c.appointments,
      sales: c.sales,
      revenue: c.revenue,
    }));
  }, [filtered]);

  const sourcePieData = useMemo(() => {
    const bySource: Record<string, { customers: number; revenue: number }> = {};
    filtered.forEach(c => {
      if (!bySource[c.source]) bySource[c.source] = { customers: 0, revenue: 0 };
      bySource[c.source].customers += c.customers;
      bySource[c.source].revenue += c.revenue;
    });
    return Object.entries(bySource).map(([name, data]) => ({ name, value: data.customers, revenue: data.revenue }));
  }, [filtered]);

  const chartTextColor = theme === 'dark' ? '#8A8680' : '#6B6560';
  const chartGridColor = theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';

  function showToast(message: string, type: 'success' | 'error' = 'success') {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  }

  function resetForm() {
    setForm({
      name: '',
      startDate: '',
      endDate: '',
      source: 'Google Ads',
      budget: 0,
      notes: '',
      active: true,
      customers: 0,
      appointments: 0,
      sales: 0,
      revenue: 0,
    });
    setEditingCampaign(null);
  }

  function openNewModal() {
    resetForm();
    const today = new Date().toISOString().split('T')[0];
    setForm(prev => ({ ...prev, startDate: today, endDate: today }));
    setModalOpen(true);
  }

  function openEditModal(campaign: Campaign) {
    setEditingCampaign(campaign);
    setForm({ ...campaign });
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    resetForm();
  }

  function handleSave() {
    if (!form.name?.trim()) {
      showToast('Kampanya adı zorunludur', 'error');
      return;
    }
    if (!form.startDate || !form.endDate) {
      showToast('Başlangıç ve bitiş tarihi zorunludur', 'error');
      return;
    }

    if (editingCampaign) {
      setCampaignList(prev => prev.map(c => c.id === editingCampaign.id ? { ...c, ...form } as Campaign : c));
      showToast('Kampanya başarıyla güncellendi');
    } else {
      const newCampaign: Campaign = {
        id: `cmp-${Date.now()}`,
        name: form.name || '',
        startDate: form.startDate || '',
        endDate: form.endDate || '',
        source: (form.source || 'Google Ads') as CampaignSource,
        customers: form.customers || 0,
        appointments: form.appointments || 0,
        sales: form.sales || 0,
        revenue: form.revenue || 0,
        active: form.active ?? true,
        budget: form.budget || 0,
        notes: form.notes || '',
      };
      setCampaignList(prev => [newCampaign, ...prev]);
      showToast('Yeni kampanya başarıyla eklendi');
    }
    closeModal();
  }

  function handleDelete(id: string) {
    setCampaignList(prev => prev.filter(c => c.id !== id));
    showToast('Kampanya silindi');
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 z-[100] space-y-2">
        {toasts.map(t => (
          <div key={t.id} className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${
            t.type === 'success' ? 'bg-[#2D5F3E] text-white' : 'bg-[#DC2626] text-white'
          }`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={t.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} />
            </div>
            {t.message}
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold font-display" style={{ color: 'var(--text-primary)' }}>Kampanyalar</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{filtered.length} kampanya kaydı</p>
        </div>
        <button onClick={openNewModal} className="spa-btn-primary text-sm flex items-center gap-2 self-start sm:self-auto whitespace-nowrap cursor-pointer">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
          Yeni Kampanya
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Toplam Müşteri', value: stats.totalCustomers.toLocaleString('tr-TR'), icon: 'ri-user-smile-line', color: '#C9A962' },
          { label: 'Toplam Gelir', value: `₺${stats.totalRevenue.toLocaleString('tr-TR')}`, icon: 'ri-coins-line', color: '#10B981' },
          { label: 'Bütçe', value: `₺${stats.totalBudget.toLocaleString('tr-TR')}`, icon: 'ri-safe-2-line', color: '#3B82F6' },
          { label: 'Dönüşüm Oranı', value: `%${stats.avgConversion}`, icon: 'ri-bar-chart-box-line', color: '#8B5CF6' },
        ].map(s => (
          <div key={s.label} className="spa-card p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-spa flex items-center justify-center flex-shrink-0" style={{ background: s.color + '12' }}>
              <div className="w-5 h-5 flex items-center justify-center"><i className={`${s.icon} text-lg`} style={{ color: s.color }} /></div>
            </div>
            <div>
              <p className="text-lg font-bold font-display" style={{ color: 'var(--text-primary)' }}>{s.value}</p>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-search-line text-sm" style={{ color: 'var(--text-muted)' }} /></div>
          <input className="spa-input pl-10" placeholder="Kampanya adı ara..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>
        <div className="flex gap-2 flex-wrap">
          <select className="spa-input text-sm min-w-[120px]" value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}>
            <option value="all">Tüm Kaynaklar</option>
            {sources.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="spa-input text-sm min-w-[100px]" value={activeFilter} onChange={e => setActiveFilter(e.target.value)}>
            <option value="all">Tümü</option>
            <option value="active">Aktif</option>
            <option value="inactive">Tamamlanan</option>
          </select>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="spa-card p-4 md:p-5">
          <h3 className="text-base font-semibold font-display mb-4" style={{ color: 'var(--text-primary)' }}>Kampanya Bazlı Dönüşüm</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                <XAxis dataKey="name" tick={{ fill: chartTextColor, fontSize: 10 }} axisLine={{ stroke: chartGridColor }} angle={-30} textAnchor="end" height={60} />
                <YAxis tick={{ fill: chartTextColor, fontSize: 12 }} axisLine={{ stroke: chartGridColor }} />
                <Tooltip contentStyle={{ background: theme === 'dark' ? '#1A1A20' : '#FFFFFF', border: `1px solid ${chartGridColor}`, borderRadius: '8px', color: theme === 'dark' ? '#E8E4DC' : '#1A1A1A' }} />
                <Bar dataKey="customers" name="Müşteri" fill="#C9A962" radius={[4, 4, 0, 0]} barSize={16} />
                <Bar dataKey="sales" name="Satış" fill="#2D5F3E" radius={[4, 4, 0, 0]} barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="spa-card p-4 md:p-5">
          <h3 className="text-base font-semibold font-display mb-4" style={{ color: 'var(--text-primary)' }}>Kaynak Dağılımı</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={sourcePieData} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={4} dataKey="value" stroke="none">
                  {sourcePieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={sourceColors[entry.name as CampaignSource] || '#9CA3AF'} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: theme === 'dark' ? '#1A1A20' : '#FFFFFF', border: `1px solid ${chartGridColor}`, borderRadius: '8px', color: theme === 'dark' ? '#E8E4DC' : '#1A1A1A' }} formatter={(value: number) => [`${value} müşteri`, '']} />
                <Legend verticalAlign="middle" align="right" layout="vertical" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: chartTextColor }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Campaign Table */}
      <div className="spa-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                {['Kampanya', 'Kaynak', 'Müşteri', 'Randevu', 'Satış', 'Gelir', 'Bütçe', 'Durum', 'İşlem'].map(h => (
                  <th key={h} className="px-3 py-3 text-left text-xs font-semibold whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id} className="border-b hover:bg-theme-hover transition-colors" style={{ borderColor: 'var(--border-color)' }}>
                  <td className="px-3 py-3">
                    <p className="font-medium" style={{ color: 'var(--text-primary)' }}>{c.name}</p>
                    <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{c.startDate} - {c.endDate}</p>
                  </td>
                  <td className="px-3 py-3">
                    <span className="spa-badge text-[10px]" style={{ background: sourceColors[c.source] + '12', color: sourceColors[c.source] }}>{c.source}</span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>{c.customers}</td>
                  <td className="px-3 py-3 whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{c.appointments}</td>
                  <td className="px-3 py-3 whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{c.sales}</td>
                  <td className="px-3 py-3 whitespace-nowrap font-semibold" style={{ color: 'var(--gold)' }}>₺{c.revenue.toLocaleString('tr-TR')}</td>
                  <td className="px-3 py-3 whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>₺{c.budget.toLocaleString('tr-TR')}</td>
                  <td className="px-3 py-3">
                    <span className="spa-badge text-[10px]" style={{ background: c.active ? 'rgba(16,185,129,0.12)' : 'rgba(156,163,175,0.12)', color: c.active ? '#10B981' : '#9CA3AF' }}>
                      {c.active ? 'Aktif' : 'Tamamlandı'}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => openEditModal(c)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-theme-hover transition-colors cursor-pointer" title="Düzenle">
                        <i className="ri-pencil-line text-sm" style={{ color: 'var(--text-muted)' }} />
                      </button>
                      <button onClick={() => handleDelete(c.id)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-50 transition-colors cursor-pointer" title="Sil">
                        <i className="ri-delete-bin-line text-sm" style={{ color: '#DC2626' }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 space-y-3">
          <div className="w-14 h-14 flex items-center justify-center rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
            <i className="ri-megaphone-line text-xl" style={{ color: 'var(--text-muted)' }} />
          </div>
          <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Kampanya bulunamadı</p>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto" onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="spa-card w-full max-w-lg flex flex-col max-h-[90vh] p-5 md:p-6 space-y-4">
            <div className="flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-bold font-display" style={{ color: 'var(--text-primary)' }}>
                {editingCampaign ? 'Kampanya Düzenle' : 'Yeni Kampanya'}
              </h2>
              <button onClick={closeModal} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-theme-hover transition-colors cursor-pointer">
                <i className="ri-close-line text-sm" style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>

            <div className="space-y-4 overflow-y-auto flex-1 min-h-0">
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Kampanya Adı <span style={{ color: '#DC2626' }}>*</span></label>
                <input className="spa-input w-full" value={form.name || ''} onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))} placeholder="Örn: Yaz Sezonu Fırsatı" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Başlangıç Tarihi <span style={{ color: '#DC2626' }}>*</span></label>
                  <input type="date" className="spa-input w-full" value={form.startDate || ''} onChange={e => setForm(prev => ({ ...prev, startDate: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Bitiş Tarihi <span style={{ color: '#DC2626' }}>*</span></label>
                  <input type="date" className="spa-input w-full" value={form.endDate || ''} onChange={e => setForm(prev => ({ ...prev, endDate: e.target.value }))} />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Kaynak</label>
                <select className="spa-input w-full" value={form.source} onChange={e => setForm(prev => ({ ...prev, source: e.target.value as CampaignSource }))}>
                  {sources.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Bütçe (₺)</label>
                  <input type="number" className="spa-input w-full" value={form.budget || 0} onChange={e => setForm(prev => ({ ...prev, budget: Number(e.target.value) }))} placeholder="0" />
                </div>
                <div className="flex items-end gap-3 pb-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.active ?? true} onChange={e => setForm(prev => ({ ...prev, active: e.target.checked }))} />
                    <span className="text-sm" style={{ color: 'var(--text-primary)' }}>Aktif</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Müşteri Sayısı</label>
                  <input type="number" className="spa-input w-full" value={form.customers || 0} onChange={e => setForm(prev => ({ ...prev, customers: Number(e.target.value) }))} placeholder="0" />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Randevu Sayısı</label>
                  <input type="number" className="spa-input w-full" value={form.appointments || 0} onChange={e => setForm(prev => ({ ...prev, appointments: Number(e.target.value) }))} placeholder="0" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Satış Sayısı</label>
                  <input type="number" className="spa-input w-full" value={form.sales || 0} onChange={e => setForm(prev => ({ ...prev, sales: Number(e.target.value) }))} placeholder="0" />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Gelir (₺)</label>
                  <input type="number" className="spa-input w-full" value={form.revenue || 0} onChange={e => setForm(prev => ({ ...prev, revenue: Number(e.target.value) }))} placeholder="0" />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Notlar</label>
                <textarea className="spa-input w-full min-h-[80px] resize-none" value={form.notes || ''} onChange={e => setForm(prev => ({ ...prev, notes: e.target.value.slice(0, 500) }))} placeholder="Kampanya notları..." maxLength={500} />
                <p className="text-[10px] text-right mt-1" style={{ color: 'var(--text-muted)' }}>{(form.notes || '').length}/500</p>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2 flex-shrink-0">
              <button onClick={closeModal} className="spa-btn-outline text-sm whitespace-nowrap cursor-pointer">İptal</button>
              <button onClick={handleSave} className="spa-btn-primary text-sm whitespace-nowrap cursor-pointer">Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}